import { useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { START_TOUCH } from '@confluence/navdex/entry-points/NavdexPointType.ts';

import type { useFollowingFeedWithSortByStorageFeedContentQuery$data } from './__generated__/useFollowingFeedWithSortByStorageFeedContentQuery.graphql.ts';
import { FeedType } from './feedTypeEnum.ts';
import type { usePopularFeedWithTimeGranularityStorageQuery$data } from './__generated__/usePopularFeedWithTimeGranularityStorageQuery.graphql.ts';

export const useFeedRenderedTrackEvent = (
	feedType: FeedType | string,
	followingFeedData: useFollowingFeedWithSortByStorageFeedContentQuery$data | undefined,
	popularFeedData: usePopularFeedWithTimeGranularityStorageQuery$data | undefined,
): void => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isFollowing = feedType === FeedType.FOLLOWING;
	const isPopular = feedType === FeedType.POPULAR;
	const hasData = isFollowing ? Boolean(followingFeedData) : Boolean(popularFeedData);
	const resultCount =
		(isFollowing
			? followingFeedData?.feed?.nodes?.length
			: popularFeedData?.popularFeed?.nodes?.length) || 0;
	const spacesFollowing = isFollowing
		? followingFeedData?.getFeedUserConfig?.spaces?.length || 0
		: undefined;
	const peopleFollowing = isFollowing
		? followingFeedData?.getFeedUserConfig?.users?.length || 0
		: undefined;

	// should fire the "rendered" analytics event only once,
	// including when switching between feed types
	const renderedEventLastFeedTypeRef = useRef('');
	const firedRenderedEvent = feedType === renderedEventLastFeedTypeRef.current;

	useEffect(() => {
		if (hasData && !firedRenderedEvent) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: 'homeScreen',
					action: 'rendered',
					actionSubject: 'feed',
					actionSubjectId: 'feedAlpha',
					attributes: {
						feedType: feedType.toLowerCase(),
						navdexPointType: START_TOUCH,
						homeVersion: 'visualRefresh',
						...(isFollowing || isPopular
							? {
									resultCount,
									// TODO: The logic of this entire hook must be reworked so that the workaround below is no longer necessary.
									// resultCount will only be 2 on SSR load, so there is no risk to this change (CCI-1817).
									isSSR: resultCount === 2,
									spacesFollowing,
									peopleFollowing,
								}
							: {}),
					},
				},
			}).fire();
			renderedEventLastFeedTypeRef.current = feedType;
		}
	}, [
		hasData,
		feedType,
		resultCount,
		spacesFollowing,
		peopleFollowing,
		firedRenderedEvent,
		isFollowing,
		isPopular,
		createAnalyticsEvent,
	]);
};
